// Import Fuse.js
// We're going to use to search integrations
import Fuse from 'fuse.js';

// Import animations
import fadeIn from '@automator/animations/fade-in';

// Import utilities
import isDefined from '@automator/utilities/is-defined';
import isEmpty from '@automator/utilities/is-empty';
import debounce from '@automator/utilities/debounce';

/**
 * Adds search functionality in the archive "Integrations" page
 */
export default class IntegrationsSearch {
	// eslint-disable-next-line require-jsdoc
	constructor() {
		// Create Fuse
		this.createFuse();

		// Listen the search field
		this.listenSearchField();
	}

	/**
	 * Listens when the user types in the search field
	 *
	 * @return {undefined}
	 */
	listenSearchField() {
		// Listen when the user types, but debounce the callback
		this.$searchField.addEventListener(
			'input',
			debounce( () => {
				// Filter integrations
				this.filter();
			}, 300 )
		);
	}

	/**
	 * Creates Fuse instance
	 *
	 * @return {undefined}
	 */
	createFuse() {
		// Add the Nodes of each integration
		this._addNodesToIntegrationsObject();

		// Add the Node of each collection
		this._addNodesToCollectionsObject();

		// Create Fuse instance
		this.fuse = new Fuse( Object.values( UncannyAutomatorBackend.integrations ), this.fuseConfig );
	}

	/**
	 * Adds Nodes to each integration in the integrations object
	 *
	 * @return {undefined}
	 */
	_addNodesToIntegrationsObject() {
		// Iterate integrations and look for the Nodes
		Object.keys( UncannyAutomatorBackend.integrations ).forEach( ( integrationID ) => {
			// Get node elements
			UncannyAutomatorBackend.integrations[integrationID].nodeList = document.querySelectorAll(
				`.uap-integrations-collections-integration[data-id="${integrationID}"]`
			);
		} );
	}

	/**
	 * Adds Node to each collection in the collections object
	 *
	 * @return {undefined}
	 */
	_addNodesToCollectionsObject() {
		// Iterate collections and look for the Nodes
		Object.keys( UncannyAutomatorBackend.collections ).forEach( ( collectionID ) => {
			// Get node elements
			UncannyAutomatorBackend.collections[collectionID].node = document.querySelector(
				`.uap-integrations-collection[data-id="${collectionID}"]`
			);
		} );
	}

	/**
	 * Filters integrations
	 *
	 * @param {[type]} searchQuery [description]
	 * @return {[type]}             [description]
	 */
	filter() {
		// Check if there is a search query selected
		if ( ! isEmpty( this.searchQuery ) ) {
			// Hide all integrations
			this._hideAllIntegrations();

			// Show all collections
			this._hideAllCollections();

			// Perform search with Fuse
			const search = this.fuse.search( this.searchQuery );

			// Check if there are results
			if ( search.length ) {
				// Iterate results
				search.forEach( ( result ) => {
					// Show the integration
					this._showIntegration( result.item.id );
				} );
			}

			// Evaluate visibiity of the collections
			this._evaluateVisibilityOfCollections( search );

			// Check if we have to show the "No results" message
			if ( ! search.length ) {
				// Show "No results"
				this._showNoResults();
			}
		} else {
			// Show all integrations
			this._showAllIntegrations();

			// Show all collections
			this._showAllCollections();

			// Hide "No results"
			this._hideNoResults();
		}
	}

	/**
	 * Hides all integrations
	 *
	 * @return {undefined}
	 */
	_hideAllIntegrations() {
		// Iterate all integrations
		this.$allIntegrations.forEach( ( $integration ) => {
			// Hide
			$integration.style.display = 'none';
		} );
	}

	/**
	 * Shows all integrations
	 *
	 * @return {undefined}
	 */
	_showAllIntegrations() {
		// Iterate all integrations
		this.$allIntegrations.forEach( ( $integration ) => {
			// Show
			fadeIn( $integration );
		} );
	}

	/**
	 * Shows an integration given a integration ID
	 *
	 * @param {Integer} integrationID The integration ID
	 * @return {undefined}
	 */
	_showIntegration( integrationID ) {
		// Display integration
		if ( isDefined( UncannyAutomatorBackend.integrations[integrationID] ) ) {
			const integration = UncannyAutomatorBackend.integrations[integrationID];

			// Iterate node elements
			integration.nodeList.forEach( ( integrationNode ) => {
				// Show
				fadeIn( integrationNode );
			} );
		}
	}

	/**
	 * Hide empty collections
	 *
	 * @param  search
	 * @return {undefined}
	 */
	_evaluateVisibilityOfCollections( search ) {
		// Get IDs of the results
		const resultsIntegrationsIDs = [];
		// Iterate results
		search.forEach( ( result ) => {
			resultsIntegrationsIDs.push( result.item.id );
		} );

		// Iterate all collections
		Object.values( UncannyAutomatorBackend.collections ).forEach( ( collection ) => {
			// Check if an integration of the results is in this collection
			const hasResult = collection.integrations.some( ( integrationID ) =>
				resultsIntegrationsIDs.includes( integrationID )
			);

			// If it has a result, show it
			if ( hasResult ) {
				collection.node.style.display = 'block';
			}
		} );
	}

	/**
	 * Shows all integrations
	 *
	 * @return {undefined}
	 */
	_showAllCollections() {
		// Iterate all integrations
		this.$allCollections.forEach( ( $collection ) => {
			// Show all
			$collection.style.display = 'block';
		} );
	}

	/**
	 * Hides all collections
	 *
	 * @return {undefined}
	 */
	_hideAllCollections() {
		// Iterate all collections
		this.$allCollections.forEach( ( $collection ) => {
			// Show it
			$collection.style.display = 'none';
		} );
	}

	/**
	 * Shows "No results" message
	 *
	 * @return {undefined}
	 */
	_showNoResults() {
		fadeIn( this.$noResults );
	}

	/**
	 * Hides "No results" message
	 *
	 * @return {undefined}
	 */
	_hideNoResults() {
		this.$noResults.style.display = 'none';
	}

	get searchQuery() {
		return this.$searchField.value;
	}

	/**
	 * Returns the node of the container with the search field
	 *
	 * @return {Node} Container with search field
	 */
	get $container() {
		return document.getElementById( 'uap-integrations-search' );
	}

	/**
	 * Returns the node of the search field
	 *
	 * @return {Node} Search field
	 */
	get $searchField() {
		return document.getElementById( 'uap-integrations-search-field' );
	}

	/**
	 * Get all integrations
	 *
	 * @return {NodeList} All integrations on the page
	 */
	get $allIntegrations() {
		return document.querySelectorAll( '.uap-integrations-collections-integration' );
	}

	/**
	 * Get all collections
	 *
	 * @return {NodeList} All collections
	 */
	get $allCollections() {
		return document.querySelectorAll(
			'.uap-integrations-collections:not(#uap-integrations-all) .uap-integrations-collection'
		);
	}

	/**
	 * Returns the "No results" element, displayed when there aren't results
	 *
	 * @return {Node} No results node
	 */
	get $noResults() {
		return document.querySelector( '.uap-integrations-collections-no-results' );
	}

	/**
	 * Returns the fuse configuration
	 *
	 * @return {Object} Fuse configuration
	 */
	get fuseConfig() {
		return {
			// keys: List of keys that will be searched. This supports nested paths, weighted search, searching in arrays of strings and objects.
			keys: [ 'integration_id', 'name' ],

			// minMatchCharLength: Only the matches whose length exceeds this value will be returned.
			minMatchCharLength: 1,

			// threshold: At what point does the match algorithm give up. A threshold of 0.0 requires a perfect match (of both letters and location), a threshold of 1.0 would match anything.
			threshold: 0.5,
		};
	}
}
